import React, { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { PriorityHigh } from '@mui/icons-material';
import { Button, Checkbox } from '@mui/material';

import ConfirmationDialog from 'commons/components/ConfirmationDialog/ConfirmationDialog.component';
import * as S from 'commons/components/Table/Table.styles';
import { ReplenishSearchCriteriaEnum } from 'commons/enums';
import { ReplenishTask } from 'commons/types';
import {
  DefaultSortProps,
  RowTitleProps,
  SortOrder,
} from 'commons/types/listPageData.interface';
import { DataTableProps } from 'commons/types/replenishItemData.interface';
import { TableActionRow, DataTable } from 'pages/dashboard/commons/components';
import {
  IOpenBulkDialogData,
  IOpenDialogData,
} from 'pages/dashboard/order-lists/type/open-dialog-state.interface';
import { RootReducerInterface } from 'redux-stores/reducers';
import { AppDispatch } from 'redux-stores/store';

import {
  OpenBulkDialogConfig,
  OpenDialogConfig,
} from '../configs/open-dialog.config';

import ActionBtnMenuReplenish from './ActionBtnMenuReplenish.component';

const rowTitles: RowTitleProps[] = [
  { title: 'SKU Code' },
  { title: 'Replenish Type' },
  { title: 'Priority', sortField: 'priority' },
  { title: 'Requestor' },
  { title: 'Replenish Date', sortField: 'queued_at' },
  { title: 'Status' },
  { title: 'Replenish User' },
  { title: 'Flag Reason' },
];

const defaultSort: DefaultSortProps = {
  sortField: 'queued_at',
  sortOrder: SortOrder.DESC,
};

const Row: React.FC<{
  data: ReplenishTask;
  onCheckboxClick: (skuCode: string) => void;
  checked: boolean;
  selectedLocation?: number;
  statusFilter?: ReplenishSearchCriteriaEnum | null;
}> = ({ data, onCheckboxClick, checked, selectedLocation, statusFilter }) => {
  const dispatch = useDispatch<AppDispatch>();
  const [open, setOpen] = useState<boolean>(false);
  const [openDialogData, setOpenDialogData] = useState<IOpenDialogData | null>(
    null,
  );

  const handleOpenDialog = (name: string) => {
    setOpenDialogData(OpenDialogConfig[name.toLocaleLowerCase()]);
    setOpen(true);
  };

  const handleCloseDialog = () => {
    setOpenDialogData(null);
    setOpen(false);
  };

  return (
    <>
      <S.TableRow>
        <S.TableColumn>
          <Checkbox
            onClick={() => onCheckboxClick(data.replenish_number)}
            checked={checked}
          />
        </S.TableColumn>
        <S.TableColumn>
          {data.is_high_priority && <PriorityHigh />}
        </S.TableColumn>
        <S.TableColumn>{data.sku_code}</S.TableColumn>
        <S.TableColumn style={{ textTransform: 'capitalize' }}>
          {data.type.replace(/_/g, ' ')}
        </S.TableColumn>
        <S.TableColumn>{data.priority || '-'}</S.TableColumn>
        <S.TableColumn>{data.requestor || '-'}</S.TableColumn>
        <S.TableColumn>
          {data.queued_at
            ? new Date(data.queued_at).toLocaleString('id-ID')
            : null}
        </S.TableColumn>
        <S.TableColumn style={{ textTransform: 'capitalize' }}>
          {data.status}
        </S.TableColumn>
        <S.TableColumn>{data.replenish_user}</S.TableColumn>
        <S.TableColumn>{data.reason}</S.TableColumn>
        <S.TableColumn style={{ textAlign: 'right' }}>
          {statusFilter !== ReplenishSearchCriteriaEnum.UPDATE_FAILED ? (
            <Button
              onClick={
                data.is_high_priority
                  ? () => handleOpenDialog('unprioritize')
                  : () => handleOpenDialog('prioritize')
              }
              variant="outlined"
              disabled={data.replenish_user !== null}
              sx={{
                width: 87,
              }}
            >
              {data.is_high_priority ? 'Unprioritize' : 'Prioritize'}
            </Button>
          ) : null}
        </S.TableColumn>
      </S.TableRow>
      <ConfirmationDialog
        title={openDialogData?.title || ''}
        content={openDialogData?.content(data.replenish_number) || ''}
        onClose={() => handleCloseDialog()}
        openModal={open}
        onConfirm={() =>
          openDialogData?.onConfirm(
            dispatch,
            data.replenish_number,
            selectedLocation,
          )
        }
      />
    </>
  );
};

const ReplenishTable: React.FC<DataTableProps> = (params): React.ReactNode => {
  const { data = [], statusFilter }: DataTableProps = params;
  const dispatch = useDispatch<AppDispatch>();

  const [checkAll, setCheckAll] = useState<boolean>(false);
  const [checkedTasks, setCheckedTasks] = useState<string[]>([]);
  const [open, setOpen] = useState<boolean>(false);
  const [openDialogData, setOpenDialogData] =
    useState<IOpenBulkDialogData | null>(null);

  const selectedTasks = data.filter((task) =>
    checkedTasks.includes(task.replenish_number),
  );

  const { selectedLocation } = useSelector(
    (state: RootReducerInterface) => state.dashboard,
  );

  const selectedReplenishNumbers = selectedTasks.map(
    (task) => task.replenish_number,
  );

  const handleCheckTask = (replenishNumber: string): void => {
    if (checkedTasks.includes(replenishNumber)) {
      // remove skuCode from checkedTasks
      checkedTasks.splice(checkedTasks.indexOf(replenishNumber), 1);
      setCheckedTasks([...checkedTasks]);
    } else {
      // insert po number to checkedOrders
      setCheckedTasks([...checkedTasks, replenishNumber]);
    }
  };

  const handleCheckAll = (): void => {
    if (checkAll) {
      // remove all check list
      setCheckedTasks([]);
    } else {
      // add all sku code to checkedTasks
      setCheckedTasks(data.map((task) => task.replenish_number));
    }
    setCheckAll(!checkAll);
  };

  const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);
  const openMenu = Boolean(anchorEl);

  const handleClickMenu = (
    event: React.MouseEvent<HTMLButtonElement>,
  ): void => {
    setAnchorEl(event.currentTarget);
  };

  const handleCloseMenu = (): void => {
    setAnchorEl(null);
  };

  const handleOpenDialog = (name: string) => {
    setOpenDialogData(OpenBulkDialogConfig[name]);
    setOpen(true);
  };

  const handleCloseDialog = () => {
    setOpenDialogData(null);
    setOpen(false);
  };

  return (
    <>
      <TableActionRow
        dataName="replenish task"
        selectedData={selectedTasks}
        anchorEl={anchorEl}
        openMenu={openMenu}
        handleClickMenu={handleClickMenu}
      >
        <ActionBtnMenuReplenish
          selectedTask={selectedTasks}
          handleCloseMenu={handleCloseMenu}
          handleOpen={handleOpenDialog}
        />
      </TableActionRow>
      <DataTable
        handleCheckAll={handleCheckAll}
        checkAll={checkAll}
        rowTitles={rowTitles}
        defaultSort={defaultSort}
        extraColumn
      >
        <S.TableBody>
          {data.map((item) => (
            <Row
              data={item}
              key={item.replenish_number}
              onCheckboxClick={handleCheckTask}
              checked={checkedTasks.includes(item.replenish_number)}
              selectedLocation={selectedLocation?.warehouse_id}
              statusFilter={statusFilter}
            />
          ))}
        </S.TableBody>
      </DataTable>
      {data.length > 0 && (
        <TableActionRow
          dataName="replenish task"
          selectedData={selectedTasks}
          anchorEl={anchorEl}
          openMenu={openMenu}
          handleClickMenu={handleClickMenu}
        >
          <ActionBtnMenuReplenish
            selectedTask={selectedTasks}
            handleCloseMenu={handleCloseMenu}
            handleOpen={handleOpenDialog}
          />
        </TableActionRow>
      )}
      <ConfirmationDialog
        title={openDialogData?.title || ''}
        content={openDialogData?.content(selectedReplenishNumbers) || ''}
        onClose={() => handleCloseDialog()}
        openModal={open}
        onConfirm={() =>
          openDialogData?.onConfirm(
            dispatch,
            selectedReplenishNumbers,
            selectedLocation?.warehouse_id,
          )
        }
      />
    </>
  );
};

export default ReplenishTable;
